import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { API_URL } from '../lib/api'

interface UpsellOffer {
  id: string
  slug: string
  name: string
  headline: string
  subheadline: string
  body_html: string
  video_url?: string
  price_cents: number
  original_price_cents?: number
  product_slug: string
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function Upsell() {
  const { offerId } = useParams<{ offerId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const productSlug = searchParams.get('product') || ''

  const [offer, setOffer] = useState<UpsellOffer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accepting, setAccepting] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(900) // 15 minutes in seconds
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!offerId) return

    const fetchUrl = productSlug
      ? `${API_URL}/api/products/${productSlug}/upsell`
      : `${API_URL}/api/upsell/${offerId}`

    fetch(fetchUrl)
      .then(res => {
        if (!res.ok) throw new Error('Offer not found')
        return res.json()
      })
      .then(data => {
        const offerData = data.data || data
        setOffer(offerData)
        setLoading(false)
      })
      .catch(() => {
        setError('This offer is no longer available.')
        setLoading(false)
      })
  }, [offerId, productSlug])

  const tick = useCallback(() => {
    setTimeRemaining(prev => {
      if (prev <= 1) {
        if (timerRef.current) clearInterval(timerRef.current)
        return 0
      }
      return prev - 1
    })
  }, [])

  useEffect(() => {
    timerRef.current = setInterval(tick, 1000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [tick])

  const handleAccept = async () => {
    if (!offer) return

    setAccepting(true)

    try {
      const token = localStorage.getItem('fc_session_token')
      const res = await fetch(`${API_URL}/api/products/${offer.product_slug}/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          upsell: true,
          offerId: offer.id,
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Something went wrong')
      }

      const data = await res.json()
      const checkoutUrl = data.checkoutUrl || data.data?.checkoutUrl
      if (checkoutUrl) {
        window.location.href = checkoutUrl
      } else {
        navigate('/dashboard')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setAccepting(false)
    }
  }

  const handleDecline = () => {
    navigate('/dashboard')
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F2EDE6',
      }}>
        <p style={{ color: '#555' }}>Loading offer...</p>
      </div>
    )
  }

  if (error && !offer) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F2EDE6',
        padding: '2rem',
      }}>
        <p style={{ color: '#555', marginBottom: '1.5rem' }}>{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '0.875rem 2rem',
            background: '#0f172a',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            fontSize: '0.95rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Go to My Library
        </button>
      </div>
    )
  }

  if (!offer) return null

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F2EDE6',
    }}>
      {/* Timer Bar */}
      {timeRemaining > 0 && (
        <div style={{
          background: '#0f172a',
          color: '#fff',
          textAlign: 'center',
          padding: '0.75rem 1rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          letterSpacing: '0.02em',
        }}>
          This offer expires in {formatTime(timeRemaining)}
        </div>
      )}
      {timeRemaining === 0 && (
        <div style={{
          background: '#dc2626',
          color: '#fff',
          textAlign: 'center',
          padding: '0.75rem 1rem',
          fontSize: '0.875rem',
          fontWeight: 500,
        }}>
          This offer has expired
        </div>
      )}

      {/* Main Content */}
      <section style={{
        maxWidth: '680px',
        margin: '0 auto',
        padding: '3rem 1.5rem 4rem',
      }}>
        {/* Headline */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: '#dc2626',
            marginBottom: '0.75rem',
          }}>
            Special One-Time Offer
          </div>
          <h1 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
            fontWeight: 500,
            color: '#0f172a',
            lineHeight: 1.3,
            marginBottom: '0.75rem',
          }}>
            {offer.headline}
          </h1>
          {offer.subheadline && (
            <p style={{
              fontSize: '1.05rem',
              color: '#555',
              lineHeight: 1.7,
            }}>
              {offer.subheadline}
            </p>
          )}
        </div>

        {/* Video */}
        {offer.video_url && (
          <div style={{
            marginBottom: '2.5rem',
            borderRadius: '5px',
            overflow: 'hidden',
            background: '#000',
            aspectRatio: '16/9',
          }}>
            <iframe
              src={offer.video_url}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="autoplay; fullscreen"
              allowFullScreen
              title="Offer video"
            />
          </div>
        )}

        {/* Body */}
        {offer.body_html && (
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '5px',
              padding: '2rem',
              marginBottom: '2.5rem',
              fontSize: '0.95rem',
              color: '#333',
              lineHeight: 1.7,
            }}
            dangerouslySetInnerHTML={{ __html: offer.body_html }}
          />
        )}

        {/* Price */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem',
        }}>
          {offer.original_price_cents && offer.original_price_cents > offer.price_cents && (
            <div style={{
              fontSize: '1rem',
              color: '#94a3b8',
              textDecoration: 'line-through',
              marginBottom: '0.25rem',
            }}>
              {formatPrice(offer.original_price_cents)}
            </div>
          )}
          <div style={{
            fontSize: '2.5rem',
            fontWeight: 600,
            color: '#0f172a',
          }}>
            {formatPrice(offer.price_cents)}
          </div>
          <div style={{
            fontSize: '0.85rem',
            color: '#94a3b8',
            marginTop: '0.25rem',
          }}>
            One-time payment
          </div>
        </div>

        {/* CTA Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <button
            onClick={handleAccept}
            disabled={accepting || timeRemaining === 0}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '1.1rem 1rem',
              background: (accepting || timeRemaining === 0) ? '#999' : '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              fontSize: '1.05rem',
              fontWeight: 500,
              cursor: (accepting || timeRemaining === 0) ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => {
              if (!accepting && timeRemaining > 0) (e.currentTarget.style.background = '#b91c1c')
            }}
            onMouseLeave={e => {
              if (!accepting && timeRemaining > 0) (e.currentTarget.style.background = '#dc2626')
            }}
          >
            {accepting
              ? 'Processing...'
              : timeRemaining === 0
                ? 'Offer Expired'
                : 'Yes, Add This To My Order'}
          </button>

          <button
            onClick={handleDecline}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '0.875rem',
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#555')}
            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
          >
            No thanks, take me to my library
          </button>
        </div>

        {error && (
          <p style={{
            color: '#dc2626',
            fontSize: '0.875rem',
            textAlign: 'center',
            marginTop: '1rem',
          }}>
            {error}
          </p>
        )}

        <p style={{
          fontSize: '0.8rem',
          color: '#999',
          textAlign: 'center',
          marginTop: '2rem',
        }}>
          Results vary. No specific outcomes are guaranteed.
        </p>
      </section>
    </div>
  )
}
